import { Injectable, NgZone } from '@angular/core';
import {
  Auth,
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  applyActionCode,
  confirmPasswordReset,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  signInWithPopup,
  verifyPasswordResetCode,
  authState,
  user as authUser,
} from '@angular/fire/auth';
import { Firestore, doc, setDoc, getDoc } from '@angular/fire/firestore';
import { GoogleAuthProvider, OAuthProvider } from '@angular/fire/auth';
import { Timestamp } from 'firebase/firestore';
import { Router } from '@angular/router';
import { PendoService } from '../../services/pendo.service';
import { catchError, defer, from, map, of, shareReplay, switchMap } from 'rxjs';

export interface Profile {
  firstname: string,
  lastname: string,
  company: string,
  emailMarketingConsent: boolean
  createdAt: Timestamp;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  readonly canAccessAiStudio$ = defer(() => authUser(this.afAuth)).pipe(
    switchMap(user => user
      ? from(user.getIdTokenResult()).pipe(
          map(result => {
            const firebase: unknown = result.claims['firebase'];
            const attributes = firebase && typeof firebase === 'object' && 'sign_in_attributes' in firebase
              ? firebase.sign_in_attributes
              : undefined;
            const groups = attributes && typeof attributes === 'object' && 'groups' in attributes
              ? attributes.groups
              : undefined;

            if (result.signInProvider !== 'oidc.authentik') return false;
            return Array.isArray(groups) && groups.some(group => group === 'trialadmin' || group === 'trialuser');
          }),
          catchError(error => {
            console.error('Unable to check AI Studio access:', error);
            return of(false);
          })
        )
      : of(false)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    public afs: Firestore,
    public afAuth: Auth,
    public router: Router,
    public ngZone: NgZone,
    private pendoService: PendoService,
  ) {}

  async signIn(email: string, password: string) {
    const result = await signInWithEmailAndPassword(this.afAuth, email, password);
    if (!result.user?.emailVerified) {
      throw new Error("auth/email-not-verified");
    }
    if (!result.user?.email) {
      throw new Error("auth/email-missing");
    }
    const token = await this.getUserToken(result.user.email.toLowerCase());
    localStorage.setItem('user', JSON.stringify(result.user));
    localStorage.setItem('token', token);

    authState(this.afAuth).subscribe((user) => {
      console.log(`authState updated with: ${JSON.stringify(user)}`);
      if (!user) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    });

    await this.ngZone.run(async () => {
      await this.router.navigate(['dashboard']);
    });
  }

  async signUp(email: string, password: string, profile: Profile) {
    await this.setUserProfile(email, profile);
    return createUserWithEmailAndPassword(this.afAuth, email, password)
      .then(async (credential) => {
        await this.sendVerificationMail(credential.user);
      });
  }

  async sendVerificationMail(user: User) {
    return sendEmailVerification(user)
      .then(async () => {
        localStorage.setItem('user', JSON.stringify({
          email: user.email
        }));
      })
      .then(() => {
        this.router.navigate(['verify-email-address']);
      });
  }

  forgotPassword(passwordResetEmail: string) {
    return sendPasswordResetEmail(this.afAuth, passwordResetEmail);
  }

  get isLoggedIn(): boolean {
    try {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const token = localStorage.getItem('token');
      return user !== null && user.emailVerified !== false && token !== null ? true : false;
    } catch (error) {
      return false;
    }
  }

  get userData(): any {
    return JSON.parse(localStorage.getItem('user')!);
  }

  googleAuth() {
    return this.authLogin(new GoogleAuthProvider());
  }

  async oidcAuth(providerId: string) {
    const provider = new OAuthProvider(providerId);
    provider.addScope('openid');
    provider.addScope('profile');
    provider.addScope('email');

    return signInWithPopup(this.afAuth, provider)
      .then(async (result) => {
        const user = result.user;
        if (!user?.email) {
          throw new Error("auth/email-missing");
        }

        const token = await this.getUserToken(user.email.toLowerCase());
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', token);

        this.ngZone.run(() => {
          this.router.navigate(['dashboard']);
        });
      })
      .catch((error) => {
        console.error('OIDC sign-in error:', error);
        throw error;
      });
  }

  authLogin(provider: any) {
    return signInWithPopup(this.afAuth, provider)
      .then(() => {
        this.ngZone.run(() => {
          this.router.navigate(['dashboard']);
        });
      })
      .catch((error) => {
        window.alert(error);
      });
  }

  setUserProfile(email: string, profile: Profile) {
    const userRef = doc(this.afs, `signups/${email.toLowerCase()}`);
    return setDoc(userRef, profile, { merge: true });
  }

  async checkProfileComplete(email: string): Promise<boolean> {
    try {
      const userRef = doc(this.afs, `users/${email.toLowerCase()}`);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return true;
      }

      const data = userDoc.data();

      if (data['group'] !== 'ED') return true;
      
      const requiredFields = ['firstname', 'lastname', 'country', 'university', 'major', 'graduationYear', 'degreeLevel'];

      return requiredFields.every(field => data && data[field]);
    } catch (error) {
      console.error('Error checking profile completeness:', error);
      return true;
    }
  }


  async getUserToken(email: string): Promise<string> {
    const userRef = doc(this.afs, `users/${email.toLowerCase()}`);
    let user: any;

    for (let i = 1; i < 100; i++) {
      console.log(`Attempt ${i}: reading token.`);
      try {
        user = await getDoc(userRef);
        if (user.data() && user.data().token) {
          console.log(`Token retrieved.`);
          if (user.data().id) {
            this.pendoService.updateVisitorId(user.data().id);
          }
          break;
        }
      } catch (e: any) {
        console.log(e);
      }
    }
    if (!user || !user.data()) {
      throw new Error('Unable to connect to ClearScape Analytics Experience backend. This usually means that your network configuration is not allowing connections to https://firestore.googleapis.com/. Please contact your network administrator to allow access to this url.');
    }
    return user!.data().token;
  }

  getOidcLogoutUrl(): string {
    const redirectUri = `${window.location.origin}/sign-in`;
    return `https://auth.ci.clearscape.teradata.com/application/o/trial-web/end-session/?post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
  }

  redirectTo(url: string) {
    window.location.assign(url);
  }

  async signOut() {
    await firebaseSignOut(this.afAuth);
    localStorage.removeItem('user');
    localStorage.removeItem('token');

    this.redirectTo(this.getOidcLogoutUrl());
  }

  async handleVerifyEmail(actionCode: string) {
    console.log(`actionCode: ${actionCode}`);
    await applyActionCode(this.afAuth, actionCode);
  }

  async handleResetPassword(actionCode: string, newPassword: string) {
    await verifyPasswordResetCode(this.afAuth, actionCode);
    await confirmPasswordReset(this.afAuth, actionCode, newPassword);
  }
}
